<script lang="ts" setup>
  import { useElementOption } from '@/composable/elementOptions'
  import { useDocument } from '@/composition'
  import { useBuilderStore } from '@/store/builder'
  import { TableCellAddAttributes } from '@/util/tableCellExtend'
  import {
    CustomLineHeight,
    ELEMENTS_META,
    ExtendedHeading,
    extractFontFamiliesFromJSON,
    FontFamilyV2,
    IElement,
    InsertGapOnPaste,
    MagicPlaceholderExtension,
    Storage,
  } from '@gohighlevel/ghl-proposals-common'
  import {
    Color,
    FontFamily,
    LineHeight,
    Link,
    RequiredExtensions,
    TextAlign,
    TextBlocks,
    TextSize,
    Underline,
  } from '@gohighlevel/ghl-text-editor'
  import BulletList from '@tiptap/extension-bullet-list'
  import Document from '@tiptap/extension-document'
  import Gapcursor from '@tiptap/extension-gapcursor'
  import Paragraph from '@tiptap/extension-paragraph'
  import Table from '@tiptap/extension-table'
  import TableCell from '@tiptap/extension-table-cell'
  import TableHeader from '@tiptap/extension-table-header'
  import TableRow from '@tiptap/extension-table-row'
  import Text from '@tiptap/extension-text'
  import TextStyle from '@tiptap/extension-text-style'
  import { EditorState } from '@tiptap/pm/state'
  import { columnResizing, tableEditing } from '@tiptap/pm/tables'
  import StarterKit from '@tiptap/starter-kit'
  import { Editor, EditorContent } from '@tiptap/vue-3'
  import { computed, onMounted, onUnmounted, PropType, reactive, ref, watch } from 'vue'
  import TableMenuComponent from '../common/TableMenuComponent.vue'

  const editor = ref()
  const store = useBuilderStore()
  const isEditable = computed(() => store.isDocumentEditable)
  const { loadingCustomValues, documentVersion } = useDocument()

  const props = defineProps({
    element: {
      type: Object as PropType<IElement>,
      required: true,
    },
    customValues: {
      type: Object as PropType<Record<string, string>>,
      required: true,
    },
  })

  const { updateOption } = useElementOption()

  const CustomBulletList = BulletList.extend({
    addCommands() {
      return {
        ...this.parent?.(),
        toggleCustomBulletList: () => editor => {
          return editor.commands.toggleBulletList()
        },
      }
    },
  })

  editor.value = new Editor({
    extensions: [
      Document,
      Paragraph,
      Text,
      MagicPlaceholderExtension.configure({
        placeholderValues: props.customValues,
        loading: loadingCustomValues.value,
      }),
      Gapcursor,
      Table.configure({
        resizable: true,
      }).extend({
        addProseMirrorPlugins() {
          return [
            columnResizing({
              handleWidth: this.options.handleWidth,
              cellMinWidth: this.options.cellMinWidth,
              View: this.options.View,
              lastColumnResizable: this.options.lastColumnResizable,
            }),
            tableEditing({
              allowTableNodeSelection: this.options.allowTableNodeSelection,
            }),
          ]
        },
      }),
      TableRow,
      TableHeader,
      TableCell.extend(TableCellAddAttributes),
      RequiredExtensions,
      TextBlocks,
      ...((documentVersion.value ?? 1) > 1 ? [ExtendedHeading] : []), // don't change the order, this should be added after TextBlocks
      StarterKit,
      TextStyle,
      FontFamily,
      ...((documentVersion.value ?? 1) === 1 ? [FontFamily] : []),
      ...((documentVersion.value ?? 1) > 1 ? [FontFamilyV2] : []),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Underline,
      Link,
      // Replace the LineHeight extension with CustomLineHeight
      ...((documentVersion.value ?? 1) > 1
        ? [
            CustomLineHeight.configure({
              defaultLineHeight: 1.5,
              types: ['paragraph', 'heading', 'blockquote', 'listItem'],
            }),
          ]
        : []),
      ...((documentVersion.value ?? 1) === 1 ? [LineHeight] : []),
      TextSize,
      Color,
      CustomBulletList,
      Storage.configure({
        storage: {
          version: props.element?.version ?? 1,
          documentVersion: documentVersion.value ?? 1,
        },
      }),
      ...((documentVersion.value ?? 1) > 1 ? [InsertGapOnPaste] : []),
    ],
    onUpdate({ editor }) {
      updateOption(ELEMENTS_META.TEXT, editor.getHTML(), props?.element?.id, props?.element?.type)
    },
    autofocus: false,
    content: props.element.component.options.text,
    editable: isEditable.value,
    parseOptions: {
      preserveWhitespace: true,
    },
    editorProps: {
      attributes: (state: EditorState) => {
        return {
          ...state.doc.attrs,
          ...((documentVersion.value ?? 1) === 1 ? { style: 'font-family: sans-serif' } : {}),
          ...(documentVersion.value > 1 ? { style: 'font-family: Open Sans' } : {}),
        }
      },
    },
  })
  watch(isEditable, () => {
    editor.value.setEditable(isEditable.value)
  })

  watch(
    () => store.moveElementDragState,
    val => {
      editor.value.setEditable(!val)
    }
  )

  // Tiptap doesn't react to its `content` prop after construction, so when the store mutates
  // `element.component.options.text` (cross-page mirror update, undo/redo, or any other write),
  // we have to explicitly push the new text into the editor. Watching the value directly means
  // Vue's reactivity does the fan-out for us. `flush: 'post'` ensures the watcher runs AFTER
  // Vue has patched the DOM with the latest cloned element so the read reflects the latest value.
  //
  // Guard with `isFocused` instead of an HTML-string compare — see the matching watcher in
  // RichTextEditor.vue for the full rationale. Tiptap's heading serialise/parse round-trip is
  // not byte-symmetric for the merged style attribute (font-size + line-height from
  // ExtendedHeading.style and text-align from TextAlign), so an HTML compare can return
  // "equal" even when a re-parse is needed for cross-page propagation. Skipping by focus
  // ensures the host editor isn't disturbed while every replica reliably re-applies the
  // canonical HTML.
  watch(
    () => props.element?.component?.options?.text,
    newText => {
      if (newText == null || !editor.value) return
      if (editor.value.isFocused) return
      // Self-stomp guard. Same rationale as the matching watcher in RichTextEditor.vue:
      // toolbar dropdown clicks (font / size, etc.) blur the editor for the duration of
      // the dropdown interaction, then the chosen command runs, mutates the prop, and
      // this watcher post-flushes while `isFocused` is briefly false. Without this
      // check, the watcher then re-applies the editor's OWN just-emitted HTML via
      // `setContent`, which resets the cursor (no selection preservation across
      // setContent) and the browser scroll-into-views the new cursor — manifesting as
      // a page-jump on tall tables. The H/F replica case is unaffected because replica
      // editors' `getHTML()` differs from `newText` (they haven't received the mirror
      // yet), so they fall through to setContent.
      if (editor.value.getHTML() === newText) return
      // `preserveWhitespace: 'full'` keeps the leading / inner spaces that the default
      // parser collapses — needed because text-block "centering" in some headers is
      // achieved by typing leading spaces (not via the `text-align` attribute), and
      // the default setContent parse drops them on non-host pages. Safe for all cases.
      editor.value.commands.setContent(newText, false, { preserveWhitespace: 'full' })
    },
    { flush: 'post' }
  )

  function onTableCellEditorFocus() {
    store.updateEditorInstance(editor.value)
  }

  function onTableCellEditorBlur() {
    const fonts = extractFontFamiliesFromJSON(editor.value?.getJSON())
    store.setLoadedFontsFromEditor(Array.from(fonts))
  }

  function onTableCellEditorCreate() {
    const fonts = extractFontFamiliesFromJSON(editor.value?.getJSON())
    store.setLoadedFontsFromEditor(Array.from(fonts))
  }

  onMounted(() => {
    editor.value.on('focus', onTableCellEditorFocus)
    if (documentVersion.value > 1) {
      editor.value.on('blur', onTableCellEditorBlur)
      editor.value.on('create', onTableCellEditorCreate)
    }
  })

  onUnmounted(() => {
    editor.value?.off('focus', onTableCellEditorFocus)
    if (documentVersion.value > 1) {
      editor.value?.off('blur', onTableCellEditorBlur)
      editor.value?.off('create', onTableCellEditorCreate)
    }
    editor.value?.destroy()
  })

  const activeElementId = computed(() => store.activeElementId)

  const state = reactive({
    isActiveElement: computed(() => {
      if (!props.element) return false
      return activeElementId.value === props.element.id
    }),
  })

  const isVersionGreaterThanOne = computed(() => {
    return documentVersion.value > 1
  })
</script>

<template>
  <TableMenuComponent v-if="state.isActiveElement" :editor="editor" />
  <div class="w-full overflow-auto">
    <EditorContent :editor="editor" :class="{ 'document-version-gt-1': isVersionGreaterThanOne }" />
  </div>
</template>
